/**
 * useOptimisticCartMutations
 *
 * Wraps SCAPI basket mutations with:
 * - Optimistic local-state quantity updates (instant UI feedback, debounced API call)
 * - Cascade: quantity/remove operations propagate to all `_childItems` of complex items
 * - Per-item removing/updating flags so the UI can overlay a spinner and hide intermediate basket
 *   re-renders caused by sequential child-removal SCAPI calls
 * - Rollback: on error the optimistic quantity overrides are cleared
 *
 * NOTE: updateItemQuantity is currently unused because the QuantityPicker is disabled (KL-930).
 * Keep this logic ready for when quantity editing is unblocked.
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import debounce from 'lodash/debounce';
import {useQueryClient} from '@tanstack/react-query';

import {useAccessToken, useCommerceApi, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast';
import {
  API_ERROR_MESSAGE,
  TOAST_MESSAGE_REMOVED_ITEM_FROM_CART,
} from '@salesforce/retail-react-app/app/constants';
import {trackRemoveFromCartFromBasketLine} from '@amblique/king-living/app/analytics/ga4/remove-from-cart';
import {
  removalGroupClearsBasket,
  resolveBasketRemovalGroup,
} from '@amblique/king-living/app/utils/cart-utils';
import {
  enqueueBasketMutation,
  evictBasketFromCustomerBasketsCache,
  getBasketFromCustomerBasketsCache,
  restoreCustomerBasketsQueries,
  snapshotCustomerBasketsQueries,
} from '@amblique/king-living/app/utils/basket-mutation-queue';
import {
  buildFlyoutDisplayMapFromLines,
  CART_FLYOUT_DISPLAY_QUERY_KEY,
  mergeFlyoutProductMaps,
} from '@amblique/king-living/app/utils/cart-flyout-display';

const DEBOUNCE_WAIT = 750;

/**
 * Return only the child line items that actually belong to this parent.
 *
 * Defends against over-broad grouping (e.g. when multiple visible parents share
 * the same productId): only operate on children whose
 * `c_parentProductLineItemUUId` matches the parent's `itemId`. Children that
 * have no UUID linking (legacy or orphaned data) are kept so single-instance
 * complex products still cascade correctly.
 */
function filterOwnChildren(lineItem) {
  const children = lineItem?._childItems ?? [];

  return children.filter((child) => {
    const linkedUUID = child?.c_parentProductLineItemUUId;

    return !linkedUUID || linkedUUID === lineItem.itemId;
  });
}

/**
 * @param {Object} [options]
 * @param {boolean} [options.retainEmptyBasket=false] - When true (checkout), never
 *   `deleteBasket` on last-item remove — keep `basketId` so the locked empty-cart
 *   flyout and checkout shipping/address state can remain. Mini-cart keeps the
 *   DE-355 last-item `deleteBasket` fast path when this is false.
 * @returns {{
 *   updateItemQuantity: (lineItem: Object, newQuantity: number) => void,
 *   removeItem: (lineItem: Object) => Promise<void>,
 *   localQuantities: Object.<string, number>,
 *   removingItemIds: Set<string>,
 *   updatingItemIds: Set<string>,
 *   isUpdating: boolean,
 *   isRemoving: boolean,
 * }}
 */
export function useOptimisticCartMutations({retainEmptyBasket = false} = {}) {
  const intl = useIntl();
  const toast = useToast();
  const queryClient = useQueryClient();
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const {data: basket} = useCurrentBasket();
  const {data: customer} = useCurrentCustomer();
  const basketId = basket?.basketId;
  const currency = basket?.currency;
  const customerId = customer?.customerId;
  const retainEmptyBasketRef = useRef(retainEmptyBasket);

  useEffect(() => {
    retainEmptyBasketRef.current = retainEmptyBasket;
  });

  // itemId → optimistic quantity override (cleared on server success / rollback on error)
  const [localQuantities, setLocalQuantities] = useState({});

  // Set of itemIds whose remove mutation is in-flight — overlays a spinner on the card
  // and hides intermediate basket re-renders caused by sequential child deletions.
  const [removingItemIds, setRemovingItemIds] = useState(new Set());

  // Set of itemIds whose quantity-update mutation is in-flight — shows a spinner overlay
  // while the debounced SCAPI call is pending and executing.
  const [updatingItemIds, setUpdatingItemIds] = useState(new Set());

  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const updateItemMutation = useShopperBasketsMutation('updateItemInBasket');
  const removeItemMutation = useShopperBasketsMutation('removeItemFromBasket');
  const deleteBasketMutation = useShopperBasketsMutation('deleteBasket');

  // Store mutations in refs so the debounced function always has the latest instance
  // without needing to be recreated every render when the mutation reference changes.
  const updateItemMutationRef = useRef(updateItemMutation);
  const removeItemMutationRef = useRef(removeItemMutation);
  const deleteBasketMutationRef = useRef(deleteBasketMutation);
  const basketIdRef = useRef(basketId);
  const customerIdRef = useRef(customerId);

  useEffect(() => {
    updateItemMutationRef.current = updateItemMutation;
  });

  useEffect(() => {
    removeItemMutationRef.current = removeItemMutation;
  });

  useEffect(() => {
    deleteBasketMutationRef.current = deleteBasketMutation;
  });

  useEffect(() => {
    basketIdRef.current = basketId;
  });

  useEffect(() => {
    customerIdRef.current = customerId;
  });

  const showError = useCallback(() => {
    toast({title: intl.formatMessage(API_ERROR_MESSAGE), status: 'error'});
  }, [intl, toast]);

  const showErrorRef = useRef(showError);

  useEffect(() => {
    showErrorRef.current = showError;
  });

  // Build debounced SCAPI call once on mount — reads current values via refs so
  // it never needs to be recreated, avoiding accidental cancellation on re-renders.
  const debouncedUpdateRef = useRef(null);

  useEffect(() => {
    debouncedUpdateRef.current = debounce(async (lineItem, newQuantity) => {
      const itemsToUpdate = [lineItem, ...filterOwnChildren(lineItem)];
      const allIds = itemsToUpdate.map((item) => item.itemId);
      const currentBasketId = basketIdRef.current;

      setIsUpdating(true);
      // Mark all related items as updating so the spinner overlay appears
      setUpdatingItemIds((prev) => {
        const next = new Set(prev);

        allIds.forEach((id) => next.add(id));

        return next;
      });

      try {
        // TODO(KINDW-43): Confirm SCAPI does NOT auto-cascade child quantities.
        // If it does, the child loop below can be removed.
        // KL-1029 — serialize the group against other basket mutations.
        await enqueueBasketMutation(async () => {
          for (const item of itemsToUpdate) {
            await updateItemMutationRef.current.mutateAsync({
              parameters: {basketId: currentBasketId, itemId: item.itemId},
              body: {productId: item.productId, quantity: newQuantity},
            });
          }
        });

        // Clear optimistic overrides once all SCAPI calls succeed
        setLocalQuantities((prev) => {
          const next = {...prev};

          allIds.forEach((id) => delete next[id]);

          return next;
        });
      } catch {
        // Rollback — remove optimistic overrides so the UI falls back to server values
        setLocalQuantities((prev) => {
          const next = {...prev};

          allIds.forEach((id) => delete next[id]);

          return next;
        });
        showErrorRef.current();
      } finally {
        setIsUpdating(false);
        setUpdatingItemIds((prev) => {
          const next = new Set(prev);

          allIds.forEach((id) => next.delete(id));

          return next;
        });
      }
    }, DEBOUNCE_WAIT);

    return () => {
      debouncedUpdateRef.current?.cancel();
    };
  }, []); // intentionally empty — reads current values via refs

  /**
   * Immediately update the displayed quantity (optimistic); debounce the SCAPI call.
   * Cascades to all _childItems of complex line items.
   */
  const updateItemQuantity = useCallback((lineItem, newQuantity) => {
    const itemsToUpdate = [lineItem, ...filterOwnChildren(lineItem)];

    setLocalQuantities((prev) => {
      const next = {...prev};

      itemsToUpdate.forEach((item) => {
        next[item.itemId] = newQuantity;
      });

      return next;
    });

    debouncedUpdateRef.current?.(lineItem, newQuantity);
  }, []);

  /** Cascade-remove configuration group (children first, then parent). */
  const removeItem = useCallback(async (lineItem) => {
    const productItems = basket?.productItems;
    const {removalOrder} = resolveBasketRemovalGroup(lineItem, productItems);
    const children = removalOrder.slice(0, -1);
    const parent = removalOrder[removalOrder.length - 1] ?? lineItem;
    const allIds = removalOrder.map((item) => item.itemId);
    // Catalog-hydrated display map only — stubs are ignored in fetchGa4OptionProductMeta.
    const displayProductsById = mergeFlyoutProductMaps(
      queryClient.getQueryData(CART_FLYOUT_DISPLAY_QUERY_KEY),
      buildFlyoutDisplayMapFromLines(productItems)
    );

    const notifyRemoved = () => {
      void trackRemoveFromCartFromBasketLine({
        api,
        currency,
        customer,
        getToken: getTokenWhenReady,
        lineItem,
        productItems,
        productsById: displayProductsById,
        queryClient,
      });

      toast({
        title: intl.formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {quantity: 1}),
        status: 'success',
      });
    };

    setRemovingItemIds((prev) => {
      const next = new Set(prev);

      allIds.forEach((id) => next.add(id));

      return next;
    });
    setIsRemoving(true);

    try {
      // KL-1029 — run the whole removal group as one serialized task so no
      // concurrent basket mutation interleaves and resurrects the item.
      // Capture identity at enqueue time; never pair one account's basket
      // snapshot with another account's live basketId (account switch while queued).
      const queuedCustomerId = customerId;
      const queuedBasketId = basketId;
      const queuedBasket = basket;

      // Re-check "group clears basket" against the live cache inside the task:
      // ATC (and other adds) may have completed while this removal waited, so a
      // stale clearsBasket flag must not deleteBasket and wipe new lines (DE-355).
      await enqueueBasketMutation(async () => {
        if (
          customerIdRef.current !== queuedCustomerId ||
          basketIdRef.current !== queuedBasketId
        ) {
          return;
        }

        if (!queuedBasketId) {
          return;
        }

        const liveBasket =
          getBasketFromCustomerBasketsCache(
            queryClient,
            queuedBasketId,
            queuedCustomerId
          ) ?? queuedBasket;
        const liveProductIds = new Set(
          (liveBasket?.productItems ?? []).map((item) => item?.itemId).filter(Boolean)
        );
        const stillPresent = (item) => liveProductIds.has(item?.itemId);
        const liveChildren = children.filter(stillPresent);
        const liveParent = stillPresent(parent) ? parent : null;
        const liveRemovalOrder = [...liveChildren, ...(liveParent ? [liveParent] : [])];
        const clearsBasketNow = removalGroupClearsBasket(liveRemovalOrder, liveBasket);

        // Shopper Baskets has no multi-item DELETE. When this group is still the
        // entire basket at execution time, one `deleteBasket` replaces N
        // child-then-parent DELETEs (mini-cart / DE-355). Skip on checkout so the
        // empty basket (and locked empty-cart flyout) retain basketId + shipping state.
        if (clearsBasketNow && !retainEmptyBasketRef.current) {
          const basketsSnapshot = snapshotCustomerBasketsQueries(queryClient);

          evictBasketFromCustomerBasketsCache(queryClient, queuedBasketId);

          try {
            await deleteBasketMutationRef.current.mutateAsync(
              {parameters: {basketId: queuedBasketId}},
              {onSuccess: notifyRemoved}
            );

            return;
          } catch {
            restoreCustomerBasketsQueries(queryClient, basketsSnapshot);
            // Fall through to child-then-parent item DELETEs if deleteBasket is rejected.
          }
        }

        if (!liveParent && liveChildren.length === 0) {
          // Group already gone (e.g. another mutation emptied the basket).
          notifyRemoved();

          return;
        }

        for (const child of liveChildren) {
          await removeItemMutationRef.current.mutateAsync({
            parameters: {basketId: queuedBasketId, itemId: child.itemId},
          });
        }

        if (liveParent) {
          await removeItemMutationRef.current.mutateAsync(
            {parameters: {basketId: queuedBasketId, itemId: liveParent.itemId}},
            {onSuccess: notifyRemoved}
          );
        } else {
          notifyRemoved();
        }
      });
    } catch {
      showError();
      // Clear removing state so the item becomes interactive again after failure
      setRemovingItemIds((prev) => {
        const next = new Set(prev);

        allIds.forEach((id) => next.delete(id));

        return next;
      });
      throw new Error('removeItem failed');
    } finally {
      setIsRemoving(false);
      // On success the item will be gone from the basket; clearing here is a safety net
      // in case a stale reference remains (e.g. optimistic list not yet reconciled).
      setRemovingItemIds((prev) => {
        const next = new Set(prev);

        allIds.forEach((id) => next.delete(id));

        return next;
      });
    }
  }, [
    api,
    basket,
    basketId,
    currency,
    customer,
    customerId,
    getTokenWhenReady,
    intl,
    queryClient,
    showError,
    toast
  ]);

  return {
    isRemoving,
    isUpdating,
    localQuantities,
    removeItem,
    removingItemIds,
    updateItemQuantity,
    updatingItemIds,
  };
}
